import React, { useEffect, useState } from "react";

import { useFuzzySearch } from "@hooks/use-fuzzy-search";
import { Checkbox } from "@shared/checkbox";

import { Accordion, type AccordionProps } from "./accordion";

interface Props<T> extends AccordionProps {
  options: {
    id: T;
    label: string;
    isSelected: boolean;
    isDisabled: boolean;
  }[];
  search?: string;
  onChange(id: T, value: boolean): void;
  onChangeAll(value: boolean): void;
  controlProps?: {
    expanded: boolean;
    setExpanded: (expanded: boolean) => void;
  };
}

const SHOW_MORE_LIMIT = 20;

export function AccordionWithCheckboxes<T>({
  title,
  options,
  isExpandedByDefault,
  onChange,
  onChangeAll,
  search,
  controlProps,
}: Props<T>) {
  const [isMoreVisible, setMoreVisible] = useState(false);
  const [filteredOptions, { setSearch }] = useFuzzySearch(options, {
    searchKeys: ["label"],
  });

  useEffect(() => {
    setSearch(search ?? "");
  }, [search, setSearch]);

  return (
    <Accordion
      title={title}
      isExpandedByDefault={isExpandedByDefault}
      controlProps={controlProps}
    >
      <div>
        <div className="text-xxs mb-5">
          <button
            className="mr-8 text-blue-500 hover:text-blue-600"
            onClick={() => onChangeAll(true)}
          >
            Select All
          </button>
          <button
            className="text-blue-500 hover:text-blue-600"
            onClick={() => onChangeAll(false)}
          >
            Clear All
          </button>
        </div>
        <div className="flex flex-wrap text-xs font-light gap-y-4 mb-4">
          {(isMoreVisible
            ? filteredOptions
            : filteredOptions.slice(0, SHOW_MORE_LIMIT)
          ).map((e) => (
            <div key={String(e.id)} className="flex-1 min-w-[13rem]">
              <Checkbox
                inputClassName="w-3 h-3 mr-0.5"
                labelClassName="leading-5"
                isDisabled={e.isDisabled}
                value={e.isSelected}
                label={e.label}
                onChange={(val) => onChange(e.id, val)}
              />
            </div>
          ))}
        </div>
        {filteredOptions.length > SHOW_MORE_LIMIT && (
          <div className="text-[10px] mb-4">
            <button
              className="text-blue-500 hover:text-blue-600"
              onClick={() => setMoreVisible(!isMoreVisible)}
            >
              {isMoreVisible ? "Show less" : "Show more"}
            </button>
          </div>
        )}
      </div>
    </Accordion>
  );
}
